import { useState, useEffect, useCallback } from "react";
import { AuthService, UserProfile, AuthTokens } from "../utils/auth";

export interface UseAuthReturn {
  // Authentication state
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserProfile | null;

  // Authentication actions
  login: (tokens: AuthTokens, user: UserProfile) => void;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;

  // Profile management
  updateProfile: (profileData: Partial<UserProfile>) => Promise<boolean>;
  fetchProfile: () => Promise<UserProfile | null>;
  changePassword: (
    currentPassword: string,
    newPassword: string
  ) => Promise<boolean>;

  // Token management
  isTokenExpiringSoon: () => boolean;
  ensureValidToken: () => Promise<boolean>;
}

export const useAuth = (): UseAuthReturn => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<UserProfile | null>(null);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);

      try {
        const isAuth = AuthService.isAuthenticated();
        const userProfile = AuthService.getUserProfile();

        if (isAuth && userProfile) {
          // Ensure token is still valid
          const tokenValid = await AuthService.ensureValidToken();
          if (tokenValid) {
            setIsAuthenticated(true);
            setUser(userProfile);
          } else {
            // Token refresh failed, clear auth state
            AuthService.clearTokens();
            setIsAuthenticated(false);
            setUser(null);
          }
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error("Auth initialization failed:", error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = useCallback((tokens: AuthTokens, userProfile: UserProfile) => {
    AuthService.setTokens(tokens);
    AuthService.setUserProfile(userProfile);
    setIsAuthenticated(true);
    setUser(userProfile);
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await AuthService.logout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  // Refresh token function
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const success = await AuthService.refreshToken();
      if (success) {
        const updatedUser = AuthService.getUserProfile();
        if (updatedUser) {
          setUser(updatedUser);
        }
        return true;
      } else {
        // Refresh failed, logout user
        setIsAuthenticated(false);
        setUser(null);
        return false;
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }
  }, []);

  // Update profile function
  const updateProfile = useCallback(
    async (profileData: Partial<UserProfile>): Promise<boolean> => {
      try {
        const success = await AuthService.updateProfile(profileData);
        if (success) {
          const updatedUser = AuthService.getUserProfile();
          if (updatedUser) {
            setUser(updatedUser);
          }
          return true;
        }
        return false;
      } catch (error) {
        console.error("Profile update failed:", error);
        return false;
      }
    },
    []
  );

  // Fetch profile function
  const fetchProfile = useCallback(async (): Promise<UserProfile | null> => {
    try {
      const profile = await AuthService.fetchProfile();
      if (profile) {
        setUser(profile);
        return profile;
      }
      return null;
    } catch (error) {
      console.error("Profile fetch failed:", error);
      return null;
    }
  }, []);

  // Change password function
  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string): Promise<boolean> => {
      try {
        return await AuthService.changePassword(currentPassword, newPassword);
      } catch (error) {
        console.error("Password change failed:", error);
        return false;
      }
    },
    []
  );

  // Token expiration check
  const isTokenExpiringSoon = useCallback((): boolean => {
    return AuthService.isTokenExpiringSoon();
  }, []);

  // Ensure valid token
  const ensureValidToken = useCallback(async (): Promise<boolean> => {
    try {
      const success = await AuthService.ensureValidToken();
      if (!success) {
        setIsAuthenticated(false);
        setUser(null);
      }
      return success;
    } catch (error) {
      console.error("Token validation failed:", error);
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }
  }, []);

  return {
    // State
    isAuthenticated,
    isLoading,
    user,

    // Actions
    login,
    logout,
    refreshToken,

    // Profile management
    updateProfile,
    fetchProfile,
    changePassword,

    // Token management
    isTokenExpiringSoon,
    ensureValidToken,
  };
};
